/**
 * RECORD-360-WORKSPACE-V1 (V1-P5) -- THE CURRENT-TRUTH CONTRACT.
 *
 * The ONE derivation of a lead record's CURRENT state for every record
 * surface: the Lead-360 full page (record header + current-state band), the
 * quick-view drawer, and any chip row that speaks about "now". Both surfaces
 * consume THIS module over the SAME role-projected `Lead360Input`, so the
 * drawer and the page can never disagree (rider R-02) and contradictory
 * current badges are impossible by construction (E-360-1/E-360-2).
 *
 * PURE + DETERMINISTIC: no React, no fetch, no `Date.now()` (callers inject
 * `nowMs`), no mutation. Same input -> same output.
 *
 * =====================================================================
 * AUTHORITY TABLE -- each field resolves from exactly ONE authority
 * =====================================================================
 * appointment state   -> the canonical appointment lifecycle resolver
 *                        `toCanonicalAppointmentStatus`
 *                        (lib/domain/appointment/appointment-status.ts:151),
 *                        applied server-side onto each card's `status` field
 *                        (buildLeadAppointmentSection,
 *                        lib/domain/appointment/appointment-intake.ts), over
 *                        the PERSISTED appointments row + the lawful
 *                        transition map APPOINTMENT_ACTION_ALLOWED_FROM.
 *                        The current-card SELECTION law lives HERE (open
 *                        stored statuses ranked furthest-along; resolved
 *                        episodes close) -- NEVER a raw
 *                        last-event-by-timestamp pick.
 * visit finalization  -> the case-sheet/visit record (card.visitStatus:
 *                        none/draft/finalized, derived from `case_sheets`
 *                        by the read model -- the same existence derivation
 *                        the clinic-day board ships).
 * treatment summary   -> plan items (card.acceptedPlanCount /
 *                        advisedPlanCount / suggestedCollectAmount -- the
 *                        day-board plan join reused verbatim; sums flow
 *                        through the #247 payment-handoff derivation).
 * payment state       -> payment records (card.paymentRecorded, the
 *                        void-excluded patient_payments join) through
 *                        `derivePaymentHandoff` VERBATIM
 *                        (frontend/src/dashboard/appointments/payment-handoff.ts).
 * identity            -> the goldenRecordLink read (#244;
 *                        record.goldenRecordLink).
 * follow-up           -> the visit/treatment outcome derivation below
 *                        (partial acceptance, advised-pending, recoverable
 *                        non-conversion -- mirrors the back-half follow-up
 *                        hook's recoverable-reason set).
 * lead conversion     -> DERIVED from the latest lawful FINALIZED
 *                        visit/opportunity truth: per-appointment outcome
 *                        resolution (plan counts + the controlled outcome
 *                        events); the LATEST signal-bearing appointment
 *                        resolves the current state; earlier contrary
 *                        verdicts become HISTORICAL (historicalWarnings).
 *                        A same-record positive+negative pair is a
 *                        SOURCE-DATA CONTRADICTION -- surfaced honestly,
 *                        never silently resolved.
 * lead stage          -> operator-owned (leads.enquiry_status), displayed
 *                        AS-IS and LABELED as the CRM stage; it never
 *                        writes and never headlines over operational truth.
 * next lawful action  -> a deterministic state machine over the states
 *                        above; the stage-contract fallback is reachable
 *                        ONLY when the record has zero appointments, so a
 *                        stale "Make first contact" is impossible by
 *                        construction post-fulfilment (E-360-4).
 *
 * PRECEDENCE (data-driven, documented; FLOW-ARCHITECTURE FLOW_188 carries
 * the canon pointer):
 *   1. OPEN MONEY FIRST (F-CT-1): a collect_pending handoff from ANY
 *      enriched episode outranks recorded / advised_only / none states
 *      from newer episodes (the newest collect wins when several exist) --
 *      a later paid visit never masks an earlier visit's open collection.
 *   2. Plan-item truth refines the binary outcome events into the two-tier
 *      tenant truth (partial vs full acceptance -- the Partha COC/CSD
 *      requirements doc, section 9) -- but COUNTS mint a verdict ONLY on a
 *      FINALIZED visit (F-CT-3: never bare counts on a draft sheet).
 *   3. POLAR SUPERSESSION ONLY (F-CT-3): the NEWEST appointment carrying a
 *      POLAR verdict (an outcome EVENT token, or accepted counts on a
 *      finalized visit) resolves lead-level conversion; historical
 *      supersession is emitted only between OPPOSING polarities; an
 *      advised-only record is an OPEN decision -- it resolves
 *      awaiting_decision only when no polar verdict exists anywhere and
 *      never supersedes or demotes one.
 *   4. SAME-EPISODE COHERENCE (F-CT-2): the treatment summary and the
 *      follow-up state derive from the conversion-resolving episode when
 *      one exists; follow-up additionally UNIONS any other episode's open
 *      advised decision (carrying whose). Source ids are exposed.
 *   5. Uncertainty stays explicit: an un-enriched wire yields
 *      'unknown_on_this_read' states -- never a fabricated value, never a
 *      silent default.
 *
 * EXPLICIT STATES, NEVER RAW NULLS: every empty/unknown case has a named
 * state below (no appointment; booked-not-arrived; fulfilled without a
 * doctor-finalized case sheet; no patient link; no payment record; no
 * treatment plan; SOURCE-DATA CONTRADICTION).
 *
 * STABLE IDS: the contract exposes the underlying record ids
 * (leadId / appointmentId / caseSheetId / patientRecordId) even where the
 * UI does not display them, so parity tests can prove the drawer and the
 * page resolved the SAME records, not merely similar labels.
 */

import type {
  Lead360AppointmentCardView,
  Lead360AppointmentSectionView,
  Lead360Input,
  Lead360Stage,
} from '@/dashboard/owner/lead-360-view';
import {
  appointmentStatusLabel,
  formatTimestampIst,
  outcomeReasonLabel,
} from '@/dashboard/owner/Lead360Panel';
import {
  isRailStage,
  NEXT_ACTION_UNKNOWN_LABEL,
  railStageLabel,
  STAGE_NEXT_ACTION,
} from '@/dashboard/leads/lead-lifecycle-rail-view';
import {
  CONFIRM_NEXT_ACTION,
  REBOOK_NEXT_ACTION,
} from '@/dashboard/leads/appointment-milestone-view';
import {
  collectCtaLabel,
  derivePaymentHandoff,
} from '@/dashboard/appointments/payment-handoff';
import {
  classMeta,
  formatLeakageAge,
  SLA_CLASS_ORDER,
  type SlaBreachClass,
} from '@/dashboard/sla-leakage/sla-leakage-view';

// ---------------------------------------------------------------------
// Outcome-signal vocabulary (the controlled appointment_events subset)
// ---------------------------------------------------------------------

/** Positive conversion signals (appointment_events tokens). */
export const POSITIVE_OUTCOME_TOKENS: readonly string[] = [
  'lead_converted',
  'treatment_accepted',
];

/** The negative conversion signal (appointment_events token). */
export const NEGATIVE_OUTCOME_TOKEN = 'lead_not_converted';

/** The advised (decision-pending) signal (appointment_events token). */
export const ADVISED_OUTCOME_TOKEN = 'treatment_advised';

/** The per-opportunity decline signal (appointment_events token). */
export const DECLINED_OUTCOME_TOKEN = 'treatment_declined';

/**
 * The recoverable non-conversion reasons -- mirror of the back-half
 * follow-up hook's RECOVERABLE_OUTCOME_REASONS
 * (lib/back-half-outcome-followup-hook.ts): these spawn a re-engagement
 * work item server-side, so the follow-up state lights for them.
 */
export const RECOVERABLE_NOT_CONVERTED_REASONS: readonly string[] = [
  'price',
  'timing',
  'no_response',
];

// ---------------------------------------------------------------------
// Contract types (explicit states -- never raw nulls)
// ---------------------------------------------------------------------

export type IdentityStateKind = 'linked' | 'possible_match' | 'none' | 'unknown';
export interface IdentityState {
  readonly kind: IdentityStateKind;
  readonly recordId: string | null;
  readonly umr: string | null;
  readonly candidateCount: number | null;
  readonly label: string;
}

export interface LeadStageState {
  readonly kind: 'recorded' | 'not_recorded';
  readonly stage: Lead360Stage | null;
  /** Engine-canon outcome label, or the honest not-recorded copy. */
  readonly label: string;
}

export type CurrentAppointmentKind =
  | 'none'
  | 'active_future'
  | 'active_past'
  | 'arrived'
  | 'checked_in'
  | 'fulfilled_open'
  | 'closed';
export interface CurrentAppointmentState {
  readonly kind: CurrentAppointmentKind;
  readonly appointmentId: number | null;
  readonly appointmentTime: string | null;
  readonly storedStatus: string | null;
  /** The canonical resolver's projection (card.status), passed through. */
  readonly canonicalStatus: string | null;
  readonly statusLabel: string | null;
}

export type CurrentVisitKind =
  | 'no_visit'
  | 'not_started'
  | 'desk_closed_no_record'
  | 'in_progress'
  | 'finalized'
  | 'unknown_on_this_read';
export interface CurrentVisitState {
  readonly kind: CurrentVisitKind;
  readonly caseSheetId: number | null;
  readonly appointmentId: number | null;
  readonly label: string;
}

export type TreatmentSummaryKind =
  | 'no_plan'
  | 'advised_only'
  | 'partially_accepted'
  | 'fully_accepted'
  | 'unknown_on_this_read';
export interface TreatmentSummaryState {
  readonly kind: TreatmentSummaryKind;
  readonly acceptedCount: number;
  readonly advisedRemainingCount: number;
  /** accepted + still-advised (the tenant opportunity denominator). */
  readonly totalPlannedCount: number;
  readonly acceptedSumInr: number | null;
  readonly label: string;
  /** The episode this summary derives from (F-CT-2 same-episode coherence:
   *  the conversion-resolving episode when one exists, else the band card).
   *  Null when no episode informs it. */
  readonly sourceAppointmentId: number | null;
}

export type ConversionKind =
  | 'none'
  | 'awaiting_decision'
  | 'converted'
  | 'partially_accepted'
  | 'not_converted'
  | 'contradiction';
export interface ConversionState {
  readonly kind: ConversionKind;
  /** Controlled reason token from the resolving not-converted event. */
  readonly reason: string | null;
  readonly label: string;
  /** The appointment whose truth resolved this state (stable-id proof). */
  readonly appointmentId: number | null;
}

export type PaymentStateKind =
  | 'recorded'
  | 'collect_pending'
  | 'advised_only_no_handoff'
  | 'none'
  | 'unknown_on_this_read';
export interface PaymentState {
  readonly kind: PaymentStateKind;
  readonly amountInr: number | null;
  readonly label: string;
  /** The episode whose money truth resolved this state (F-CT-1: an open
   *  collect from ANY enriched episode outranks newer recorded/advised/none
   *  states; the newest collect wins when several exist). */
  readonly sourceAppointmentId: number | null;
}

export interface FollowUpState {
  readonly kind: 'required' | 'none';
  readonly whyLabel: string | null;
  /** The episode holding the open follow-up (F-CT-2: the resolving episode
   *  for its own reasons; the episode with the open advised decision for
   *  the union leg). Null when none. */
  readonly sourceAppointmentId: number | null;
}

export interface OwnerState {
  readonly kind: 'assigned' | 'pool' | 'unknown';
  readonly label: string | null;
  readonly isMine: boolean;
  readonly escalated: boolean;
}

/**
 * MANAGER-OPS-COCKPIT-V2 (rider R-29): the band's SLA/waiting chip state,
 * resolved from the record wire's `slaState` seam field. EXPLICIT states
 * only (charter Scope 1b: never raw nulls): a wire without the seam field
 * resolves to 'unavailable' (honest), a classified-clear record to 'clear',
 * breaches to 'breach' with the worst severity + a plain-language label.
 */
export type SlaChipKind = 'breach' | 'clear' | 'unavailable';
export interface SlaChipState {
  readonly kind: SlaChipKind;
  /** 'neutral' unless kind==='breach' (then the worst breach severity). */
  readonly severity: 'neutral' | 'amber' | 'red';
  /** How many active breaches (0 unless kind==='breach'). */
  readonly breachCount: number;
  readonly label: string;
  /**
   * LEAD-RECORD-POLISH-BATCH-V1 (CC-02) -- the SLA CLOCK, rendered from THE
   * ONE V4-P0 classifier and nothing else. All three are null unless the wire
   * carried the matching classifier output:
   *   - `waitingLabel`  the oldest breach's waiting age ("Waiting 2h 15m").
   *   - `slaWindowLabel` the breached class's own window ("SLA 30m") -- only
   *     when the seam passed `thresholdMinutes` through.
   *   - `overdueLabel`  age MINUS window ("Overdue by 1h 45m") -- only when
   *     both numbers came from the classifier.
   * A clear / unavailable record leaves all three null: the record NEVER
   * computes a waiting clock of its own (the Attack-First rule "SLA math from
   * the ONE classifier").
   */
  readonly waitingLabel: string | null;
  readonly slaWindowLabel: string | null;
  readonly overdueLabel: string | null;
  /**
   * The classifier's OWN waiting age in whole minutes (the number behind
   * `waitingLabel`), or null when it did not classify one. Surfaced so a
   * summary tile can lead with the ENGINE's age instead of an independently
   * measured elapsed clock -- the two anchor differently (the engine measures
   * from the class's own anchor: capture / qualification / last activity), and
   * showing both would let the page contradict itself.
   */
  readonly waitingMinutes: number | null;
}

export type NextActionSource =
  | 'appointment'
  | 'visit'
  | 'payment'
  | 'conversion'
  | 'stage'
  | 'unknown';
export interface NextLawfulAction {
  readonly label: string;
  readonly source: NextActionSource;
}

export interface HistoricalWarnings {
  readonly present: boolean;
  readonly labels: readonly string[];
}

export interface RecordStableIds {
  readonly leadId: number | null;
  readonly appointmentId: number | null;
  readonly caseSheetId: number | null;
  readonly patientRecordId: string | null;
}

/** THE named contract every record surface consumes. */
export interface LeadRecordCurrentTruth {
  readonly identityState: IdentityState;
  readonly currentLeadStage: LeadStageState;
  readonly currentAppointment: CurrentAppointmentState;
  readonly currentVisit: CurrentVisitState;
  readonly currentTreatmentSummary: TreatmentSummaryState;
  readonly currentConversionState: ConversionState;
  readonly currentPaymentState: PaymentState;
  readonly currentFollowUpState: FollowUpState;
  readonly currentOwner: OwnerState;
  readonly currentSlaState: SlaChipState;
  readonly nextLawfulAction: NextLawfulAction;
  readonly historicalWarnings: HistoricalWarnings;
  readonly stableIds: RecordStableIds;
}

// ---------------------------------------------------------------------
// Copy (the ONE vocabulary both surfaces render; tests pin these)
// ---------------------------------------------------------------------

export const CONVERSION_NONE_LABEL = 'No outcome yet';
export const CONVERSION_AWAITING_LABEL = 'Advised - decision pending';
/** The reserved commercial token. COMMERCIAL-LANGUAGE-AND-NAME-TRUTH-
 *  CORRECTIVE-V1 (V-1): "Converted" is the payment-derived commercial state
 *  ONLY (the #267 canon, deriveCommercialConversionState). On this
 *  visit-outcome plane it renders ONLY behind active non-voided first-payment
 *  evidence -- see acceptedOutcomeLabel. */
export const CONVERSION_CONVERTED_LABEL = 'Converted';
/** The accepted visit-outcome fact BEFORE payment (V-1 one-word-one-fact):
 *  acceptance is "Treatment accepted", never "Converted". */
export const CONVERSION_TREATMENT_ACCEPTED_LABEL = 'Treatment accepted';
export const CONVERSION_CONTRADICTION_LABEL =
  'Conflicting records - needs review';

/**
 * The accepted-outcome label, PAYMENT-GATED (V-1 REQ-COMMERCIAL-VOCABULARY-
 * UNIFICATION): a positive visit-outcome reads "Treatment accepted" until an
 * ACTIVE non-voided first payment exists, then upgrades to the reserved
 * "Converted". `hasActiveFirstPayment` is the void-excluded paymentRecorded
 * signal, so a later void drops it to false and the label falls back to the
 * accepted/payment-pending stage automatically (zero Converted remnants).
 */
export function acceptedOutcomeLabel(hasActiveFirstPayment: boolean): string {
  return hasActiveFirstPayment
    ? CONVERSION_CONVERTED_LABEL
    : CONVERSION_TREATMENT_ACCEPTED_LABEL;
}
export const VISIT_NO_VISIT_LABEL = 'No visit yet';
export const VISIT_NOT_STARTED_LABEL = 'Visit not started';
/** E-360-5 honest copy: desk-fulfilled without a doctor record. */
export const VISIT_DESK_CLOSED_LABEL =
  'Closed at the desk - no doctor visit record';
export const VISIT_IN_PROGRESS_LABEL = 'In consultation';
export const VISIT_FINALIZED_LABEL = 'Visit finalized by the doctor';
export const VISIT_UNKNOWN_LABEL = 'Visit state unavailable';
export const PAYMENT_RECORDED_LABEL = 'Payment recorded';
export const PAYMENT_NONE_LABEL = 'No payment activity';
export const PAYMENT_ADVISED_ONLY_LABEL = 'No payment due - advised only';
export const PAYMENT_UNKNOWN_LABEL = 'Payment state unavailable';
/** D-3: the exact prior-visit outstanding-collection label prefix. */
export const PAYMENT_PRIOR_VISIT_PREFIX = 'Outstanding collection from prior visit';
export const IDENTITY_NONE_LABEL = 'No patient record linked';
export const IDENTITY_POSSIBLE_LABEL = 'Possible existing patient';
export const IDENTITY_UNKNOWN_LABEL = 'Patient link unknown';
export const NEXT_ACTION_EXPECTED =
  'Patient expected - receive and check in on arrival';
export const NEXT_ACTION_BEGIN_CONSULT = 'Begin the consultation';
export const NEXT_ACTION_CLOSE_VISIT =
  'Close the visit in the consult workspace';
export const NEXT_ACTION_RECORD_OUTCOME = 'Record the visit outcome';
export const NEXT_ACTION_REVIEW_CONFLICT =
  'Review the conflicting outcome records';
export const NEXT_ACTION_FOLLOW_UP_REMAINING =
  'Follow up on the remaining advised treatment';
export const NEXT_ACTION_FOLLOW_UP_DECISION =
  'Follow up on the advised treatment decision';
export const NEXT_ACTION_REENGAGE = 'Plan the re-engagement follow-up';
export const NEXT_ACTION_RECOVER = 'Attempt recovery - offer a new appointment';
export const FOLLOW_UP_REMAINING_WHY =
  'Remaining advised treatment to follow up';
export const FOLLOW_UP_AWAITING_WHY = 'Advised treatment awaiting decision';
/** R-29 SLA chip copy (explicit states; never a raw null, never a fake clear). */
export const SLA_CLEAR_LABEL = 'No SLA breach';
export const SLA_UNAVAILABLE_LABEL = 'SLA not available on this read';
/** Count-only title when a single breach carries an unknown class token
 *  (never echo a foreign token raw -- the clinical-canary posture). */
export const SLA_ONE_BREACH_FALLBACK_TITLE = '1 SLA breach';

/** The known V4-P0 class tokens (render-time validation of wire tokens). */
const KNOWN_SLA_CLASSES: ReadonlySet<string> = new Set<string>(SLA_CLASS_ORDER);
function isKnownSlaClass(v: string): v is SlaBreachClass {
  return KNOWN_SLA_CLASSES.has(v);
}

/** Whole-INR display (payment-handoff idiom: en-IN grouping). */
function inr(amount: number): string {
  return `Rs ${amount.toLocaleString('en-IN')}`;
}

/**
 * The pinned partial-acceptance rendering (Scope 1c fixture): counts + one
 * sum, never a binary Converted collapse, never a procedure name.
 * "Partially accepted - 1 of 2 treatments accepted - Rs 5,000 accepted -
 *  follow-up required for remaining"
 */
export function partialAcceptanceLabel(
  acceptedCount: number,
  totalPlannedCount: number,
  acceptedSumInr: number | null,
): string {
  const money = acceptedSumInr !== null ? ` - ${inr(acceptedSumInr)} accepted` : '';
  return (
    `Partially accepted - ${acceptedCount} of ${totalPlannedCount} treatments ` +
    `accepted${money} - follow-up required for remaining`
  );
}

export function notConvertedLabel(reason: string | null): string {
  return reason !== null && reason.trim() !== ''
    ? `Not converted (${outcomeReasonLabel(reason)})`
    : 'Not converted';
}

// ---------------------------------------------------------------------
// Per-record outcome resolution (shared by the day board -- ONE derivation)
// ---------------------------------------------------------------------

export interface OutcomeSignalInput {
  /** This record's outcome tokens (any order; presence is what matters). */
  readonly outcomeEvents: readonly string[];
  /** Plan-item counts when the wire carries them (else undefined). */
  readonly acceptedPlanCount?: number;
  readonly advisedPlanCount?: number;
}

/**
 * Resolve ONE record's conversion kind from its outcome signals. The law:
 *   - positive (lead_converted / treatment_accepted / accepted plan items)
 *     AND negative (lead_not_converted) together = CONTRADICTION -- surfaced,
 *     never silently resolved (the Scope 1d authority ruling owns the
 *     write-side fix).
 *   - positive only -> converted; refined to partially_accepted when the
 *     plan counts prove remaining advised items (the tenant two-tier truth).
 *   - negative only -> not_converted. A declined-only record (treatment
 *     declined, nothing accepted) is the not_converted tier -- the same
 *     matrix deriveVisitClose applies (consult-close.ts): a decline BESIDE
 *     an acceptance is partial-acceptance reality, never a contradiction.
 *   - advised-only signals -> awaiting_decision.
 *   - nothing -> none.
 * Consumed by the current-truth contract AND the clinic-day board chips, so
 * no surface anywhere can render Converted + Not converted simultaneously.
 */
export function resolveOutcomeSignals(input: OutcomeSignalInput): ConversionKind {
  const tokens = new Set(input.outcomeEvents);
  const accepted = input.acceptedPlanCount ?? 0;
  const advisedRemaining = input.advisedPlanCount ?? 0;
  const positive =
    POSITIVE_OUTCOME_TOKENS.some((t) => tokens.has(t)) || accepted > 0;
  const negative = tokens.has(NEGATIVE_OUTCOME_TOKEN);
  if (positive && negative) return 'contradiction';
  if (positive) {
    return accepted > 0 && advisedRemaining > 0 ? 'partially_accepted' : 'converted';
  }
  if (negative || tokens.has(DECLINED_OUTCOME_TOKEN)) return 'not_converted';
  if (tokens.has(ADVISED_OUTCOME_TOKEN) || advisedRemaining > 0) {
    return 'awaiting_decision';
  }
  return 'none';
}

// ---------------------------------------------------------------------
// Card helpers (selection law -- the contract's appointment authority)
// ---------------------------------------------------------------------

const OPEN_STORED_STATUSES: readonly string[] = [
  'booked',
  'confirmed',
  'arrived',
  'checked_in',
  'fulfilled',
];
const ACTIVE_STORED: readonly string[] = ['booked', 'confirmed'];
const TERMINAL_STORED_STATUSES: readonly string[] = [
  'cancelled',
  'no_show',
  'completed',
];
const OPEN_STATUS_RANK: Readonly<Record<string, number>> = {
  booked: 0,
  confirmed: 0,
  arrived: 1,
  checked_in: 2,
  fulfilled: 3,
};

/** This card's outcome tokens, in recorded order (empty when no history). */
export function cardOutcomeTokens(card: Lead360AppointmentCardView): string[] {
  const relevant = new Set([
    ...POSITIVE_OUTCOME_TOKENS,
    NEGATIVE_OUTCOME_TOKEN,
    ADVISED_OUTCOME_TOKEN,
    'treatment_declined',
  ]);
  return (card.history?.events ?? [])
    .map((e) => e.eventType as string)
    .filter((t) => relevant.has(t));
}

/** The card's latest not-converted (or declined) reason token, or null. */
function cardNotConvertedReason(card: Lead360AppointmentCardView): string | null {
  const events = card.history?.events ?? [];
  for (let i = events.length - 1; i >= 0; i -= 1) {
    const e = events[i];
    if (
      e.eventType === 'lead_not_converted' ||
      (e.eventType as string) === DECLINED_OUTCOME_TOKEN
    ) {
      if (typeof e.outcomeReason === 'string' && e.outcomeReason.trim() !== '') {
        return e.outcomeReason;
      }
      if (e.eventType === 'lead_not_converted') return null;
    }
  }
  return null;
}

/** True when the card carries an explicit POLAR outcome EVENT token
 *  (positive: lead_converted / treatment_accepted; negative:
 *  lead_not_converted / treatment_declined). treatment_advised is NOT
 *  polar -- an advised record is an OPEN decision (F-CT-3). */
export function hasPolarEventToken(card: Lead360AppointmentCardView): boolean {
  const tokens = new Set(cardOutcomeTokens(card));
  return (
    POSITIVE_OUTCOME_TOKENS.some((t) => tokens.has(t)) ||
    tokens.has(NEGATIVE_OUTCOME_TOKEN) ||
    tokens.has(DECLINED_OUTCOME_TOKEN)
  );
}

/**
 * F-CT-3 POLAR VERDICT: an outcome EVENT token, OR accepted plan counts on
 * a FINALIZED visit -- never bare counts on a draft sheet (a mid-consult
 * draft's counts are the doctor's work in progress, not a verdict).
 */
export function cardHasPolarVerdict(card: Lead360AppointmentCardView): boolean {
  if (hasPolarEventToken(card)) return true;
  return card.visitStatus === 'finalized' && (card.acceptedPlanCount ?? 0) > 0;
}

/**
 * Per-card conversion resolution. F-CT-3: plan COUNTS feed the resolution
 * ONLY on a FINALIZED visit (events always count) -- a draft sheet's counts
 * never mint a verdict mid-consult.
 */
export function resolveCardConversion(
  card: Lead360AppointmentCardView,
): ConversionKind {
  const finalized = card.visitStatus === 'finalized';
  return resolveOutcomeSignals({
    outcomeEvents: cardOutcomeTokens(card),
    acceptedPlanCount: finalized ? card.acceptedPlanCount : undefined,
    advisedPlanCount: finalized ? card.advisedPlanCount : undefined,
  });
}

/**
 * A card is RESOLVED-CLOSED (a finished episode -- never a "current"
 * appointment) when its stored status is terminal (cancelled / no_show /
 * completed) OR it is fulfilled AND the visit is doctor-finalized OR it
 * carries an explicit outcome EVENT token (D-1: bare plan counts NEVER
 * close a fulfilled card -- a desk-fulfilled row with a draft sheet stays
 * OPEN so outcome recording is never impossible; the E-360-8 probe). This
 * still WIDENS the workbench's original fulfilled+not-converted-only closed
 * law (a doctor-finalized CONVERTED visit no longer stays open offering
 * stale conversion actions).
 */
export function isCardResolvedClosed(card: Lead360AppointmentCardView): boolean {
  if (TERMINAL_STORED_STATUSES.includes(card.storedStatus)) return true;
  if (card.storedStatus !== 'fulfilled') return false;
  return card.visitStatus === 'finalized' || hasPolarEventToken(card);
}

/**
 * The record's single CURRENT (open, unresolved) appointment card, or null.
 * Furthest-along stored status wins; ties break to the later time, then the
 * higher id (the workbench's established law, restated here as the ONE
 * selection authority).
 */
export function selectCurrentAppointmentCard(
  section: Lead360AppointmentSectionView | undefined,
): Lead360AppointmentCardView | null {
  if (!section || !section.hasAppointments) return null;
  const open = section.cards.filter(
    (c) =>
      OPEN_STORED_STATUSES.includes(c.storedStatus) && !isCardResolvedClosed(c),
  );
  if (open.length === 0) return null;
  return [...open].sort((a, b) => {
    const ra = OPEN_STATUS_RANK[a.storedStatus] ?? 0;
    const rb = OPEN_STATUS_RANK[b.storedStatus] ?? 0;
    if (ra !== rb) return rb - ra;
    const ta = Date.parse(a.appointmentTime);
    const tb = Date.parse(b.appointmentTime);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.appointmentId - a.appointmentId;
  })[0] as Lead360AppointmentCardView;
}

/** Cards newest-first (time desc, id desc) -- the scan order for "latest". */
function cardsNewestFirst(
  section: Lead360AppointmentSectionView | undefined,
): Lead360AppointmentCardView[] {
  if (!section || !section.hasAppointments) return [];
  return [...section.cards].sort((a, b) => {
    const ta = Date.parse(a.appointmentTime);
    const tb = Date.parse(b.appointmentTime);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return tb - ta;
    return b.appointmentId - a.appointmentId;
  });
}


// ---------------------------------------------------------------------
// Field resolvers
// ---------------------------------------------------------------------

function resolveIdentity(record: Lead360Input): IdentityState {
  const link = record.goldenRecordLink;
  if (link === undefined) {
    return {
      kind: 'unknown',
      recordId: null,
      umr: null,
      candidateCount: null,
      label: IDENTITY_UNKNOWN_LABEL,
    };
  }
  if (link.status === 'linked') {
    const umr = link.umr ?? null;
    return {
      kind: 'linked',
      recordId: link.recordId ?? null,
      umr,
      candidateCount: null,
      label: umr !== null ? `Linked to patient record (${umr})` : 'Linked to patient record',
    };
  }
  if (link.status === 'possible_match') {
    return {
      kind: 'possible_match',
      recordId: null,
      umr: null,
      candidateCount: link.candidateCount ?? null,
      label: IDENTITY_POSSIBLE_LABEL,
    };
  }
  return {
    kind: 'none',
    recordId: null,
    umr: null,
    candidateCount: null,
    label: IDENTITY_NONE_LABEL,
  };
}

function resolveStage(record: Lead360Input): LeadStageState {
  const stage = isRailStage(record.stage) ? record.stage : null;
  return {
    kind: stage !== null ? 'recorded' : 'not_recorded',
    stage,
    label: railStageLabel(record.stage),
  };
}

function resolveCurrentAppointment(
  current: Lead360AppointmentCardView | null,
  latest: Lead360AppointmentCardView | null,
  nowMs: number,
): CurrentAppointmentState {
  const card = current ?? latest;
  if (card === null) {
    return {
      kind: 'none',
      appointmentId: null,
      appointmentTime: null,
      storedStatus: null,
      canonicalStatus: null,
      statusLabel: null,
    };
  }
  let kind: CurrentAppointmentKind;
  if (current === null) {
    kind = 'closed';
  } else if (ACTIVE_STORED.includes(card.storedStatus)) {
    const t = Date.parse(card.appointmentTime);
    // A malformed time reads future (never a false "date has passed") --
    // the appointment-milestone-view posture.
    kind = !Number.isFinite(t) || t >= nowMs ? 'active_future' : 'active_past';
  } else if (card.storedStatus === 'arrived') {
    kind = 'arrived';
  } else if (card.storedStatus === 'checked_in') {
    kind = 'checked_in';
  } else {
    kind = 'fulfilled_open';
  }
  return {
    kind,
    appointmentId: card.appointmentId,
    appointmentTime: card.appointmentTime,
    storedStatus: card.storedStatus,
    canonicalStatus: card.status ?? null,
    statusLabel: appointmentStatusLabel(card),
  };
}

const VISIT_LABELS: Readonly<Record<CurrentVisitKind, string>> = {
  no_visit: VISIT_NO_VISIT_LABEL,
  not_started: VISIT_NOT_STARTED_LABEL,
  desk_closed_no_record: VISIT_DESK_CLOSED_LABEL,
  in_progress: VISIT_IN_PROGRESS_LABEL,
  finalized: VISIT_FINALIZED_LABEL,
  unknown_on_this_read: VISIT_UNKNOWN_LABEL,
};

function resolveVisit(
  card: Lead360AppointmentCardView | null,
): CurrentVisitState {
  if (card === null) {
    return {
      kind: 'no_visit',
      caseSheetId: null,
      appointmentId: null,
      label: VISIT_LABELS.no_visit,
    };
  }
  let kind: CurrentVisitKind;
  if (card.visitStatus === undefined) {
    kind = 'unknown_on_this_read';
  } else if (card.visitStatus === 'finalized') {
    kind = 'finalized';
  } else if (card.visitStatus === 'draft') {
    kind = 'in_progress';
  } else if (
    card.storedStatus === 'fulfilled' ||
    card.storedStatus === 'completed'
  ) {
    // E-360-5: the legal two-object truth -- the appointment closed at the
    // desk (incl. the R-1 front-desk fast path) with no doctor record.
    kind = 'desk_closed_no_record';
  } else {
    kind = 'not_started';
  }
  return {
    kind,
    caseSheetId: card.caseSheetId ?? null,
    appointmentId: card.appointmentId,
    label: VISIT_LABELS[kind],
  };
}

function resolveTreatmentSummary(
  card: Lead360AppointmentCardView | null,
): TreatmentSummaryState {
  if (card === null) {
    // F-CT-4: no episode at all is a DETERMINATE no-plan state, never an
    // "unknown" (there is nothing a better read could reveal).
    return {
      kind: 'no_plan',
      acceptedCount: 0,
      advisedRemainingCount: 0,
      totalPlannedCount: 0,
      acceptedSumInr: null,
      label: 'No treatment plan',
      sourceAppointmentId: null,
    };
  }
  if (card.visitStatus === undefined) {
    return {
      kind: 'unknown_on_this_read',
      acceptedCount: 0,
      advisedRemainingCount: 0,
      totalPlannedCount: 0,
      acceptedSumInr: null,
      label: 'Treatment plan unavailable',
      sourceAppointmentId: card.appointmentId,
    };
  }
  const accepted = card.acceptedPlanCount ?? 0;
  const advisedRemaining = card.advisedPlanCount ?? 0;
  const total = accepted + advisedRemaining;
  const sum = card.suggestedCollectAmount ?? null;
  if (accepted > 0 && advisedRemaining > 0) {
    return {
      kind: 'partially_accepted',
      acceptedCount: accepted,
      advisedRemainingCount: advisedRemaining,
      totalPlannedCount: total,
      acceptedSumInr: sum,
      label: partialAcceptanceLabel(accepted, total, sum),
      sourceAppointmentId: card.appointmentId,
    };
  }
  if (accepted > 0) {
    return {
      kind: 'fully_accepted',
      acceptedCount: accepted,
      advisedRemainingCount: 0,
      totalPlannedCount: total,
      acceptedSumInr: sum,
      label: sum !== null ? `Accepted - ${inr(sum)}` : 'Treatment accepted',
      sourceAppointmentId: card.appointmentId,
    };
  }
  if (advisedRemaining > 0) {
    return {
      kind: 'advised_only',
      acceptedCount: 0,
      advisedRemainingCount: advisedRemaining,
      totalPlannedCount: total,
      acceptedSumInr: null,
      label: `Advised - ${advisedRemaining} treatment${advisedRemaining === 1 ? '' : 's'} awaiting decision`,
      sourceAppointmentId: card.appointmentId,
    };
  }
  return {
    kind: 'no_plan',
    acceptedCount: 0,
    advisedRemainingCount: 0,
    totalPlannedCount: 0,
    acceptedSumInr: null,
    label: 'No treatment plan',
    sourceAppointmentId: card.appointmentId,
  };
}

function conversionLabelFor(
  kind: ConversionKind,
  reason: string | null,
  summary: TreatmentSummaryState,
  hasActiveFirstPayment: boolean,
): string {
  switch (kind) {
    case 'none':
      return CONVERSION_NONE_LABEL;
    case 'awaiting_decision':
      return CONVERSION_AWAITING_LABEL;
    case 'converted':
      // V-1: the reserved "Converted" only behind active first-payment
      // evidence; acceptance alone reads "Treatment accepted".
      return acceptedOutcomeLabel(hasActiveFirstPayment);
    case 'partially_accepted':
      return summary.kind === 'partially_accepted'
        ? summary.label
        : partialAcceptanceLabel(
            summary.acceptedCount,
            summary.totalPlannedCount,
            summary.acceptedSumInr,
          );
    case 'not_converted':
      return notConvertedLabel(reason);
    case 'contradiction':
      return CONVERSION_CONTRADICTION_LABEL;
    default: {
      const _exhaustive: never = kind;
      void _exhaustive;
      return CONVERSION_NONE_LABEL;
    }
  }
}

const CONVERTED_POLARITY: readonly ConversionKind[] = [
  'converted',
  'partially_accepted',
];

function resolveConversion(
  section: Lead360AppointmentSectionView | undefined,
  summaryOfResolvingCard: (card: Lead360AppointmentCardView) => TreatmentSummaryState,
): {
  state: ConversionState;
  historical: string[];
  resolvingCard: Lead360AppointmentCardView | null;
} {
  const newestFirst = cardsNewestFirst(section);

  // F-CT-3 POLAR SUPERSESSION ONLY: the resolving card is the NEWEST card
  // carrying a POLAR verdict (an outcome EVENT token, or accepted counts on
  // a FINALIZED visit). Advised-only records are OPEN decisions: they
  // resolve awaiting_decision ONLY when no polar verdict exists anywhere,
  // and they never supersede or demote a polar verdict.
  const polarCards = newestFirst.filter(cardHasPolarVerdict);
  if (polarCards.length === 0) {
    const advisedCard =
      newestFirst.find(
        (c) => resolveCardConversion(c) === 'awaiting_decision',
      ) ?? null;
    if (advisedCard !== null) {
      return {
        state: {
          kind: 'awaiting_decision',
          reason: null,
          label: CONVERSION_AWAITING_LABEL,
          appointmentId: advisedCard.appointmentId,
        },
        historical: [],
        resolvingCard: advisedCard,
      };
    }
    return {
      state: {
        kind: 'none',
        reason: null,
        label: CONVERSION_NONE_LABEL,
        appointmentId: null,
      },
      historical: [],
      resolvingCard: null,
    };
  }

  const resolving = polarCards[0];
  const kind = resolveCardConversion(resolving);
  const reason = kind === 'not_converted' ? cardNotConvertedReason(resolving) : null;
  const label = conversionLabelFor(
    kind,
    reason,
    summaryOfResolvingCard(resolving),
    resolving.paymentRecorded === true,
  );

  // Earlier POLAR cards whose verdict OPPOSES the resolving polarity are
  // HISTORICAL -- labeled, never silently dropped and never a current badge.
  // Advised-only cards are never compared (they are open, not contrary).
  const historical: string[] = [];
  for (const older of polarCards.slice(1)) {
    const olderKind = resolveCardConversion(older);
    if (olderKind === 'none' || olderKind === 'awaiting_decision') continue;
    const samePolarity =
      (CONVERTED_POLARITY.includes(kind) && CONVERTED_POLARITY.includes(olderKind)) ||
      kind === olderKind;
    if (samePolarity) continue;
    const olderReason =
      olderKind === 'not_converted' ? cardNotConvertedReason(older) : null;
    historical.push(
      `Superseded: ${conversionLabelFor(olderKind, olderReason, summaryOfResolvingCard(older), older.paymentRecorded === true)} - ${formatTimestampIst(older.appointmentTime)}`,
    );
  }
  if (kind === 'contradiction') {
    historical.unshift(
      'This visit carries both a converted and a not-converted record',
    );
  }
  return {
    state: { kind, reason, label, appointmentId: resolving.appointmentId },
    historical,
    resolvingCard: resolving,
  };
}

/**
 * F-CT-1 MONEY-FIRST ACROSS ALL EPISODES: a collect_pending handoff from
 * ANY enriched episode outranks recorded / advised_only / none states from
 * newer episodes -- open money is never masked by a later paid visit
 * (adversarial finding P4, CLOSED). The NEWEST collect wins when several
 * exist; within each non-collect kind the newest episode speaks.
 *
 * D-3 LABEL: when the collect's source episode is NOT the current
 * appointment, the label is the prior-visit outstanding-collection line
 * (`PAYMENT_PRIOR_VISIT_PREFIX - Rs <amount>`); otherwise the existing
 * collect label stands.
 */
function resolvePayment(
  section: Lead360AppointmentSectionView | undefined,
  currentAppointmentId: number | null,
): PaymentState {
  const newestFirst = cardsNewestFirst(section);
  if (newestFirst.length === 0) {
    return {
      kind: 'none',
      amountInr: null,
      label: PAYMENT_NONE_LABEL,
      sourceAppointmentId: null,
    };
  }
  let sawEnrichment = false;
  let newestCollect: { card: Lead360AppointmentCardView; amount: number | null } | null =
    null;
  let newestRecorded: Lead360AppointmentCardView | null = null;
  let newestAdvisedOnly: Lead360AppointmentCardView | null = null;
  for (const card of newestFirst) {
    if (card.visitStatus === undefined) continue;
    sawEnrichment = true;
    const handoff = derivePaymentHandoff({
      status: card.storedStatus,
      visitStatus: card.visitStatus,
      paymentRecorded: card.paymentRecorded,
      acceptedPlanCount: card.acceptedPlanCount,
      advisedPlanCount: card.advisedPlanCount,
      suggestedCollectAmount: card.suggestedCollectAmount,
    });
    if (handoff.kind === 'collect' && newestCollect === null) {
      newestCollect = { card, amount: handoff.amount };
    } else if (handoff.kind === 'recorded' && newestRecorded === null) {
      newestRecorded = card;
    } else if (handoff.kind === 'advised_only' && newestAdvisedOnly === null) {
      newestAdvisedOnly = card;
    }
  }
  if (newestCollect !== null) {
    const { card, amount } = newestCollect;
    const priorVisit =
      currentAppointmentId !== null && card.appointmentId !== currentAppointmentId;
    return {
      kind: 'collect_pending',
      amountInr: amount,
      label: priorVisit
        ? amount !== null
          ? `${PAYMENT_PRIOR_VISIT_PREFIX} - ${inr(amount)}`
          : PAYMENT_PRIOR_VISIT_PREFIX
        : amount !== null
          ? `Payment pending - ${inr(amount)} suggested`
          : 'Payment pending',
      sourceAppointmentId: card.appointmentId,
    };
  }
  if (newestRecorded !== null) {
    return {
      kind: 'recorded',
      amountInr: null,
      label: PAYMENT_RECORDED_LABEL,
      sourceAppointmentId: newestRecorded.appointmentId,
    };
  }
  if (newestAdvisedOnly !== null) {
    return {
      kind: 'advised_only_no_handoff',
      amountInr: null,
      label: PAYMENT_ADVISED_ONLY_LABEL,
      sourceAppointmentId: newestAdvisedOnly.appointmentId,
    };
  }
  if (!sawEnrichment) {
    return {
      kind: 'unknown_on_this_read',
      amountInr: null,
      label: PAYMENT_UNKNOWN_LABEL,
      sourceAppointmentId: null,
    };
  }
  return {
    kind: 'none',
    amountInr: null,
    label: PAYMENT_NONE_LABEL,
    sourceAppointmentId: null,
  };
}

/**
 * F-CT-2: follow-up derives from the conversion-RESOLVING episode's own
 * reasons first (partial acceptance / awaiting decision / recoverable
 * non-conversion), then UNIONS in any OTHER episode holding an open advised
 * decision -- carrying WHOSE episode owes the follow-up
 * (sourceAppointmentId). One resolved visit never hides another episode's
 * open decision.
 */
function resolveFollowUp(
  conversion: ConversionState,
  summary: TreatmentSummaryState,
  section: Lead360AppointmentSectionView | undefined,
): FollowUpState {
  const primarySource = conversion.appointmentId ?? summary.sourceAppointmentId;
  if (conversion.kind === 'partially_accepted' || summary.kind === 'partially_accepted') {
    return {
      kind: 'required',
      whyLabel: FOLLOW_UP_REMAINING_WHY,
      sourceAppointmentId: primarySource,
    };
  }
  if (conversion.kind === 'awaiting_decision' || summary.kind === 'advised_only') {
    return {
      kind: 'required',
      whyLabel: FOLLOW_UP_AWAITING_WHY,
      sourceAppointmentId: primarySource,
    };
  }
  if (
    conversion.kind === 'not_converted' &&
    conversion.reason !== null &&
    RECOVERABLE_NOT_CONVERTED_REASONS.includes(conversion.reason)
  ) {
    return {
      kind: 'required',
      whyLabel: `Re-engagement follow-up (${outcomeReasonLabel(conversion.reason)})`,
      sourceAppointmentId: primarySource,
    };
  }
  // UNION leg: the newest OTHER episode with an open advised decision.
  const openAdvised = cardsNewestFirst(section).find(
    (c) =>
      c.appointmentId !== conversion.appointmentId &&
      resolveCardConversion(c) === 'awaiting_decision',
  );
  if (openAdvised !== undefined) {
    return {
      kind: 'required',
      whyLabel: FOLLOW_UP_AWAITING_WHY,
      sourceAppointmentId: openAdvised.appointmentId,
    };
  }
  return { kind: 'none', whyLabel: null, sourceAppointmentId: null };
}

function resolveOwner(record: Lead360Input): OwnerState {
  const ownership = record.ownerAssignment;
  if (ownership === undefined) {
    return { kind: 'unknown', label: null, isMine: false, escalated: false };
  }
  if (ownership.assigned) {
    return {
      kind: 'assigned',
      label: ownership.ownerLabel ?? 'Assigned',
      isMine: ownership.isMine,
      escalated: false,
    };
  }
  return {
    kind: 'pool',
    label: null,
    isMine: false,
    escalated: ownership.escalated,
  };
}

/**
 * V1-P6 CORRECTIVE (founder live finding 2026-07-11, lead 93; BRANCH B --
 * WRONG-EPISODE PAYMENT DOOR): the ONE appointment id the record surfaces'
 * Record-payment door may carry. The door consumes THE CONTRACT, never an
 * independent episode pick (the retired first-non-cancelled-card heuristic
 * sent a Rs 8,888 payment to episode 44 while the obligation lived on the
 * collect source episode):
 *   - collect_pending -> the obligation's EXACT sourceAppointmentId (a
 *     payment recorded through the door nets against the episode that is
 *     owed -- Branch B's required fix);
 *   - otherwise -> the CURRENT episode's id when one resolves AND it is not
 *     terminal-negative (advances and future payments are lawful; the door
 *     never disappears), else null (a patient-level entry with no
 *     appointment prefill). A cancelled/no_show latest episode NEVER
 *     prefills (adversarial finding 1: steering a new payment onto a
 *     terminal-negative episode is the same wrong-episode class -- the
 *     retired picker's null-on-terminal contract is preserved).
 */
export function paymentDoorAppointmentId(
  truth: LeadRecordCurrentTruth,
): number | null {
  if (truth.currentPaymentState.kind === 'collect_pending') {
    return truth.currentPaymentState.sourceAppointmentId;
  }
  const current = truth.currentAppointment;
  if (current.storedStatus === 'cancelled' || current.storedStatus === 'no_show') {
    return null;
  }
  return current.appointmentId;
}

/**
 * MANAGER-OPS-COCKPIT-V2 (rider R-29): resolve the band's SLA/waiting chip
 * from the record wire's seam field. Explicit states only:
 *   - seam field ABSENT -> 'unavailable' (SLA_UNAVAILABLE_LABEL; honest, the
 *     fail-safe-omit posture -- never a fabricated "clear").
 *   - classified, zero breaches -> 'clear' (SLA_CLEAR_LABEL).
 *   - ONE breach whose class is a KNOWN engine token -> its plain-language
 *     title + the waiting age ("New leads waiting too long - waiting 2h
 *     15m"); an unknown/foreign class token renders through the count-only
 *     fallback, NEVER echoed raw (clinical-canary posture).
 *   - MULTIPLE breaches -> the count + the OLDEST waiting age.
 * Severity = the worst across breaches (red > amber).
 */
export function resolveSla(record: Lead360Input): SlaChipState {
  const sla = record.slaState;
  // Defensive: a malformed seam value (breaches missing / non-array) resolves
  // to the honest unavailable state -- never a crash, never a fake clear.
  if (sla === undefined || !Array.isArray(sla.breaches)) {
    return {
      kind: 'unavailable',
      severity: 'neutral',
      breachCount: 0,
      label: SLA_UNAVAILABLE_LABEL,
      waitingLabel: null,
      slaWindowLabel: null,
      overdueLabel: null,
      waitingMinutes: null,
    };
  }
  const breaches = sla.breaches;
  if (breaches.length === 0) {
    return {
      kind: 'clear',
      severity: 'neutral',
      breachCount: 0,
      label: SLA_CLEAR_LABEL,
      waitingLabel: null,
      slaWindowLabel: null,
      overdueLabel: null,
      waitingMinutes: null,
    };
  }
  const severity = breaches.some((b) => b.severity === 'red') ? 'red' : 'amber';
  // The OLDEST breach leads (the engine's own ordering intent: oldest first
  // within a class). Its age AND its window are the two classifier numbers the
  // clock renders from -- nothing here recomputes either.
  const oldest = breaches.reduce(
    (worst, b) =>
      Number.isFinite(b.ageMinutes) && b.ageMinutes > (worst?.ageMinutes ?? -1)
        ? b
        : worst,
    breaches[0]!,
  );
  const oldestAge = Number.isFinite(oldest.ageMinutes) ? oldest.ageMinutes : 0;
  const waiting = `waiting ${formatLeakageAge(oldestAge)}`;
  // CC-02: the window + the overdue delta ride ONLY when the classifier's own
  // thresholdMinutes reached this wire (an older backend omits it -> the chip
  // shows the waiting age alone and claims no window).
  const window =
    typeof oldest.thresholdMinutes === 'number' &&
    Number.isFinite(oldest.thresholdMinutes) &&
    oldest.thresholdMinutes > 0
      ? oldest.thresholdMinutes
      : null;
  const clock = {
    waitingMinutes: oldestAge,
    waitingLabel: `Waiting ${formatLeakageAge(oldestAge)}`,
    slaWindowLabel: window !== null ? `SLA ${formatLeakageAge(window)}` : null,
    overdueLabel:
      window !== null && oldestAge > window
        ? `Overdue by ${formatLeakageAge(oldestAge - window)}`
        : null,
  };
  if (breaches.length === 1) {
    const only = breaches[0]!;
    const title = isKnownSlaClass(only.breachClass)
      ? classMeta(only.breachClass).title
      : SLA_ONE_BREACH_FALLBACK_TITLE;
    return {
      kind: 'breach',
      severity,
      breachCount: 1,
      label: `${title} - ${waiting}`,
      ...clock,
    };
  }
  return {
    kind: 'breach',
    severity,
    breachCount: breaches.length,
    label: `${breaches.length} SLA breaches - ${waiting}`,
    ...clock,
  };
}

function resolveNextAction(input: {
  stage: LeadStageState;
  appointment: CurrentAppointmentState;
  visit: CurrentVisitState;
  conversion: ConversionState;
  payment: PaymentState;
  latestCard: Lead360AppointmentCardView | null;
}): NextLawfulAction {
  const { stage, appointment, visit, conversion, payment, latestCard } = input;

  // 1. No appointment history at all -> the stage contract is the ONLY
  //    lawful source (and the only path to "Make first contact ...").
  if (appointment.kind === 'none') {
    if (stage.stage !== null) {
      return { label: STAGE_NEXT_ACTION[stage.stage], source: 'stage' };
    }
    return { label: NEXT_ACTION_UNKNOWN_LABEL, source: 'unknown' };
  }

  // 2. An OPEN appointment drives the action.
  if (appointment.kind === 'active_future') {
    return {
      label:
        appointment.storedStatus === 'confirmed'
          ? NEXT_ACTION_EXPECTED
          : CONFIRM_NEXT_ACTION,
      source: 'appointment',
    };
  }
  if (appointment.kind === 'active_past') {
    return { label: REBOOK_NEXT_ACTION, source: 'appointment' };
  }
  if (appointment.kind === 'arrived') {
    return { label: STAGE_NEXT_ACTION.arrived, source: 'appointment' };
  }
  if (appointment.kind === 'checked_in') {
    return { label: NEXT_ACTION_BEGIN_CONSULT, source: 'appointment' };
  }
  if (appointment.kind === 'fulfilled_open') {
    if (visit.kind === 'in_progress') {
      return { label: NEXT_ACTION_CLOSE_VISIT, source: 'visit' };
    }
    if (payment.kind === 'collect_pending') {
      return { label: collectCtaLabel(payment.amountInr), source: 'payment' };
    }
    return { label: NEXT_ACTION_RECORD_OUTCOME, source: 'visit' };
  }

  // 3. Every episode closed -> money first, then the conversion truth.
  if (payment.kind === 'collect_pending') {
    return { label: collectCtaLabel(payment.amountInr), source: 'payment' };
  }
  if (conversion.kind === 'contradiction') {
    return { label: NEXT_ACTION_REVIEW_CONFLICT, source: 'conversion' };
  }
  if (conversion.kind === 'partially_accepted') {
    return { label: NEXT_ACTION_FOLLOW_UP_REMAINING, source: 'conversion' };
  }
  if (conversion.kind === 'awaiting_decision') {
    // A finalized advised-only visit: the outcome IS recorded (advised);
    // the lawful next act is the decision follow-up -- never "record the
    // visit outcome" (a dead door on a closed record; adversarial-review
    // finding 1).
    return { label: NEXT_ACTION_FOLLOW_UP_DECISION, source: 'conversion' };
  }
  if (conversion.kind === 'converted') {
    return {
      label: STAGE_NEXT_ACTION.converted_to_revenue,
      source: 'conversion',
    };
  }
  if (conversion.kind === 'not_converted') {
    return { label: NEXT_ACTION_REENGAGE, source: 'conversion' };
  }
  if (
    latestCard !== null &&
    (latestCard.storedStatus === 'cancelled' || latestCard.storedStatus === 'no_show')
  ) {
    return { label: NEXT_ACTION_RECOVER, source: 'appointment' };
  }
  // Closed with no verdict (e.g. desk-closed, no plan signals): record it.
  return { label: NEXT_ACTION_RECORD_OUTCOME, source: 'visit' };
}

// ---------------------------------------------------------------------
// THE resolver
// ---------------------------------------------------------------------

export function resolveLeadRecordCurrentTruth(
  record: Lead360Input,
  nowMs: number,
): LeadRecordCurrentTruth {
  const section = record.appointmentSection;
  const currentCard = selectCurrentAppointmentCard(section);
  const newestFirst = cardsNewestFirst(section);
  const latestCard = newestFirst[0] ?? null;
  const bandCard = currentCard ?? latestCard;

  const identityState = resolveIdentity(record);
  const currentLeadStage = resolveStage(record);
  const currentAppointment = resolveCurrentAppointment(currentCard, latestCard, nowMs);
  // currentAppointment/currentVisit speak the CURRENT episode's operational
  // truth; the money/conversion/treatment planes resolve across episodes.
  const currentVisit = resolveVisit(bandCard);
  const conversion = resolveConversion(section, resolveTreatmentSummary);
  // F-CT-2 SAME-EPISODE COHERENCE: the treatment summary derives from the
  // conversion-RESOLVING episode when one exists (else the band card; else
  // the determinate no-plan state).
  const currentTreatmentSummary = resolveTreatmentSummary(
    conversion.resolvingCard ?? bandCard,
  );
  const currentPaymentState = resolvePayment(
    section,
    currentAppointment.appointmentId,
  );
  const currentFollowUpState = resolveFollowUp(
    conversion.state,
    currentTreatmentSummary,
    section,
  );
  const currentOwner = resolveOwner(record);
  const currentSlaState = resolveSla(record);
  const nextLawfulAction = resolveNextAction({
    stage: currentLeadStage,
    appointment: currentAppointment,
    visit: currentVisit,
    conversion: conversion.state,
    payment: currentPaymentState,
    latestCard,
  });

  const leadIdNum = Number(record.safeLeadId);
  return {
    identityState,
    currentLeadStage,
    currentAppointment,
    currentVisit,
    currentTreatmentSummary,
    currentConversionState: conversion.state,
    currentPaymentState,
    currentFollowUpState,
    currentOwner,
    currentSlaState,
    nextLawfulAction,
    historicalWarnings: {
      present: conversion.historical.length > 0,
      labels: conversion.historical,
    },
    stableIds: {
      leadId:
        Number.isSafeInteger(leadIdNum) && leadIdNum > 0 ? leadIdNum : null,
      appointmentId: currentAppointment.appointmentId,
      caseSheetId: currentVisit.caseSheetId,
      patientRecordId: identityState.recordId,
    },
  };
}
